// Consulting2.jsx
import React, { useRef, useEffect, useState, memo, useCallback } from 'react';
import { useRive, Layout, Fit, Alignment, decodeImage } from '@rive-app/react-canvas';
import gsap from 'gsap';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import chaosLottie from '../assets/lottie/chaos.lottie';
import strategyLottie from '../assets/lottie/strategy.lottie';
import boardrive from '../assets/rive/board.riv';
import boardrivebg from '../assets/rive/board-animation-bg.webp';
import bg_notification from '../assets/consulting/bg_notification.webp';

const Consulting2 = () => {
  const notificationRefs = useRef([]);
  const cardLeftRef = useRef(null);
  const cardRightRef = useRef(null);
  const [leftPlayer, setLeftPlayer] = useState(null);
  const [rightPlayer, setRightPlayer] = useState(null);
  
  // Memoize the play/pause handlers to prevent unnecessary re-renders
  const handleLeftPlay = useCallback((play) => {
    if (leftPlayer) {
      if (play) {
        leftPlayer.play();
        gsap.to(leftPlayer.container, {
          opacity: 1,
          duration: 0.2,
        });
      } else {
        leftPlayer.pause();
        gsap.to(leftPlayer.container, {
          opacity: 0.5,
          duration: 0.2,
        });
      }
    }
  }, [leftPlayer]);

  const handleRightPlay = useCallback((play) => {
    if (rightPlayer) {
      if (play) {
        rightPlayer.play();
        gsap.to(rightPlayer.container, {
          opacity: 1,
          duration: 0.2,
        });
      } else {
        rightPlayer.pause();
        gsap.to(rightPlayer.container, {
          opacity: 0.5,
          duration: 0.2,
        });
      }
    }
  }, [rightPlayer]);

  // Memoize mouse and touch event handlers
  const handleLeftMouseEnter = useCallback(() => handleLeftPlay(false), [handleLeftPlay]);
  const handleLeftMouseLeave = useCallback(() => handleLeftPlay(true), [handleLeftPlay]);
  const handleRightMouseEnter = useCallback(() => handleRightPlay(false), [handleRightPlay]);
  const handleRightMouseLeave = useCallback(() => handleRightPlay(true), [handleRightPlay]);
  
  // Touch event handlers
  const handleLeftTouchStart = useCallback(() => handleLeftPlay(false), [handleLeftPlay]);
  const handleLeftTouchEnd = useCallback(() => handleLeftPlay(true), [handleLeftPlay]);
  const handleRightTouchStart = useCallback(() => handleRightPlay(false), [handleRightPlay]);
  const handleRightTouchEnd = useCallback(() => handleRightPlay(true), [handleRightPlay]);

  // State to track if Rive should be loaded
  const [shouldLoadRive, setShouldLoadRive] = useState(false);
  const riveContainerRef = useRef(null);

  // Intersection Observer for lazy loading Lottie and Rive animations
  useEffect(() => {
    const leftObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          leftObserver.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const rightObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          rightObserver.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const riveObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadRive(true);
          riveObserver.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px' } // Slightly larger margin since this is the background
    );

    if (cardLeftRef.current) {
      leftObserver.observe(cardLeftRef.current);
    }

    if (cardRightRef.current) {
      rightObserver.observe(cardRightRef.current);
    }

    if (riveContainerRef.current) {
      riveObserver.observe(riveContainerRef.current);
    }

    return () => {
      leftObserver.disconnect();
      rightObserver.disconnect();
      riveObserver.disconnect();
    };
  }, []);
  // Custom asset loader to swap out the black background inside the .riv file
  const assetLoader = (asset) => {
    if (asset.isImage && (asset.name === "scheme" || asset.name === "bg")) {
      fetch(boardrivebg)
        .then((res) => res.arrayBuffer())
        .then((buffer) => decodeImage(new Uint8Array(buffer)))
        .then((img) => {
          asset.setRenderImage(img);
          img.unref();
        });
      return true; // prevent default loading
    }
    return false;
  };

  // Only pass the Rive config when shouldLoadRive is true
  const riveConfig = shouldLoadRive ? {
    src: boardrive,
    animations: ['glow-orange', 'glow-blue'],
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
    assetLoader,
  } : null;

  const { RiveComponent, rive } = useRive(riveConfig);

  // Play the animations when Rive loads
  useEffect(() => {
    if (rive) {
      rive.play('glow-orange');
      rive.play('glow-blue');
      //console.log('Playing glow-orange and glow-blue animations');
    }
  }, [rive]);


  return (
    <section className="relative w-full min-h-[1080px] bg-[#070709] overflow-visible flex justify-center">
      <div className="relative w-full max-w-[100vw] h-auto aspect-[16/9] sm:w-[1920px] sm:h-[1080px] sm:absolute sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2">
        {/* Background image with responsive dimensions */}
        <img
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-contain"
          src={boardrivebg}
        />

        {/* Rive animation with responsive dimensions */}
        <div className="absolute inset-0 w-full h-full" ref={riveContainerRef}>
          {shouldLoadRive && (
            <RiveComponent
              style={{
                width: '100%',
                height: '100%',
                background: 'transparent',
                objectFit: 'contain'
              }}
            />
          )}
        </div>

        {/* Gradient Blur */}
        {/*<div className="absolute top-0 h-[120px] w-full -translate-y-[75%] rounded-[100%] bg-gradient-to-b from-black via-black via-[90%] to-transparent blur-[36px]"></div>*/}

        {/* Noise overlay */}
        <div
          className="noise absolute inset-0 z-0 opacity-5 mix-blend-soft-light"
          aria-hidden="true"
        ></div>

        {/* Center line */}
        <span
          className="absolute left-1/2 top-0 -z-10 h-full w-px"
          aria-hidden="true"
        ></span>

        {/* Cards Container */}
        <div className="absolute -top-12 sm:-top-20 md:-top-70 left-1/2 transform -translate-x-1/2 z-50 flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-8 w-full px-4 sm:px-0 justify-center">
          <div className="group bg-[#0D0E12] rounded-2xl p-4 sm:p-6 h-[250px] sm:h-[300px] md:h-[350px] w-full max-w-[90vw] sm:max-w-none sm:w-[300px] md:w-[350px] lg:w-[438px] overflow-hidden relative">
            {/* Background Image */}
            <img 
              src={bg_notification}
              alt=""
              loading="lazy"              
              className="absolute top-0 left-0 w-full h-full object-cover opacity-100"
            />
            {/* Subtle Black Overlay - REMOVED to fix hover issue */}
            {/*<div className="absolute inset-0 bg-black/0 z-10"></div>*/}
            {/* Window control circles and hover text - Top bar */}
            <div className="absolute top-6 left-6 right-6 flex justify-between items-center z-10">
              <div className="flex space-x-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#787c87]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#787c87]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#787c87]"></div>
              </div>
              <>
                <span className="text-[#787c87] font-['Geist'] font-normal text-[12px] tracking-[0.02em] hidden md:block">Hover to stop</span>
                <span className="text-[#787c87] font-['Geist'] font-normal text-[12px] tracking-[0.02em] block md:hidden">Tap to stop</span>
              </>
            </div>

            <div 
              className="relative h-full w-full" 
              ref={cardLeftRef}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  onMouseEnter={handleLeftMouseEnter}
                  onMouseLeave={handleLeftMouseLeave}
                  onTouchStart={handleLeftTouchStart}
                  onTouchEnd={handleLeftTouchEnd}
                  style={{ width: '100%', height: '100%' }}
                >
                  <DotLottieReact 
                    dotLottieRefCallback={(player) => { 
                      if (player) setLeftPlayer(player);
                    }}
                    src={chaosLottie}
                    autoplay={true}
                    loop={true}
                    style={{
                      width: '100%',
                      height: '100%',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="font-['Geist'] text-[16px] sm:text-[20px] text-white/50 font-semibold py-2 sm:py-0">VS</div>
          
          <div className="group bg-[#0D0E12] rounded-2xl p-4 sm:p-6 h-[250px] sm:h-[300px] md:h-[350px] w-full max-w-[90vw] sm:max-w-none sm:w-[300px] md:w-[350px] lg:w-[438px] overflow-hidden relative">
            {/* BLCKS Logo - Top right corner */}
            {/*<div className="absolute top-4 right-4 z-10">
              <img 
                src="/icons/BLCKS_LOGO_NEW.svg" 
                alt="BLCKS Logo"
                className="h-6 w-auto"
              />
            </div>*/}
            {/* Background Image */}
            <img 
              src={bg_notification}
              alt=""
              loading="lazy"
              className="absolute top-0 left-0 w-full h-full object-cover opacity-100"
            />
            {/* Window control circles and hover text - Top bar */}
            <div className="absolute top-6 left-6 right-6 flex justify-between items-center z-10">
              <div className="flex space-x-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#787c87]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#787c87]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#787c87]"></div>
              </div>
              <>
                <span className="text-[#787c87] font-['Geist'] font-normal text-[12px] tracking-[0.02em] hidden md:block">Hover to stop</span>
                <span className="text-[#787c87] font-['Geist'] font-normal text-[12px] tracking-[0.02em] block md:hidden">Tap to stop</span>
              </>
            </div>
            <div 
              className="relative h-full w-full" 
              ref={cardRightRef}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  onMouseEnter={handleRightMouseEnter}
                  onMouseLeave={handleRightMouseLeave}
                  onTouchStart={handleRightTouchStart}
                  onTouchEnd={handleRightTouchEnd}
                  style={{ width: '100%', height: '100%' }}
                >
                  <DotLottieReact
                    dotLottieRefCallback={(player) => { 
                      if (player) setRightPlayer(player);
                    }}
                    src={strategyLottie}
                    autoplay={true}
                    loop={true}
                    style={{
                      width: '100%',
                      height: '100%',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(Consulting2);
